"use client";
import { useState } from 'react';
import { useTransition, animated } from '@react-spring/web';
import { X, Bell, Loader2 } from 'lucide-react';
import { GOOGLE_SCRIPT_URL } from '@/lib/config';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for phone field - only allow + and numbers
    if (name === 'phone') {
      const cleanedValue = value.replace(/[^+\d]/g, '');
      setFormData(prev => ({
        ...prev,
        phone: cleanedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El correo electrónico no es válido';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    } else if (!/^\+?\d+$/.test(formData.phone)) {
      newErrors.phone = 'El teléfono debe contener solo números y puede empezar con +';
    }
    
    if (!formData.consent) {
      newErrors.consent = 'Debes aceptar la política de privacidad';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Send data to Google Sheets with a "Waitlist" indicator
      const url = GOOGLE_SCRIPT_URL;
      
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `Nombre=${encodeURIComponent(formData.name)}&Email=${encodeURIComponent(formData.email)}&Phone=${encodeURIComponent(formData.phone)}&Consentimiento=${formData.consent ? 'true' : 'false'}&Tipo=Lista de Espera`
      });
      
      const data = await response.text();
      console.log('Google Sheets response:', data);
      
      // Show success message
      alert('¡Te has registrado exitosamente en la lista de espera! Te contactaremos cuando se abran las inscripciones para la próxima edición.');
      
      // Reset form and close modal
      setFormData({ name: '', email: '', phone: '', consent: false });
      onClose();
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Hubo un error al enviar los datos. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const transitions = useTransition(isOpen, {
    from: { opacity: 0, transform: 'scale(0.95)' },
    enter: { opacity: 1, transform: 'scale(1)' },
    leave: { opacity: 0, transform: 'scale(0.95)' },
    config: { duration: 300 }
  });

  return (
    <>
      {transitions((style, item) =>
        item ? (
          <div key="waitlist-modal">
            {/* Backdrop */}
            <animated.div
              style={{ opacity: style.opacity }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            
            {/* Modal - Full Screen */}
            <animated.div
              style={style}
              className="fixed inset-0 bg-transparent z-50 overflow-hidden overflow-y-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="waitlist-modal-title"
            >
              <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-md mx-auto">
                  {/* Header */}
                  <div className="relative bg-gradient-to-r from-red-500 to-red-600 p-4 sm:p-6 text-center rounded-t-3xl">
                    <button
                      onClick={onClose}
                      className="absolute right-4 top-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                      aria-label="Cerrar formulario de lista de espera"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                    
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Bell className="w-8 h-8 text-red-500" />
                    </div>
                    
                    <h2 id="waitlist-modal-title" className="text-xl sm:text-2xl font-heading uppercase tracking-wider text-white mb-2">
                      Lista de Espera - Próxima Edición
                    </h2>
                    <p className="text-sm sm:text-base text-white/90">
                      Sé el primero en enterarte cuando se abran las inscripciones
                    </p>
                  </div>
                  
                  {/* Form */}
                  <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 space-y-4 rounded-b-3xl shadow-xl">
                    {/* Name */}
                    <div>
                      <label htmlFor="waitlist-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Tu nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="waitlist-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all`}
                        placeholder="Juan García"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    
                    {/* Email */}
                    <div>
                      <label htmlFor="waitlist-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Tu correo electrónico <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="waitlist-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all`}
                        placeholder="juan@ejemplo.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    
                    {/* Phone */}
                    <div>
                      <label htmlFor="waitlist-phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Tu teléfono (con prefijo) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="waitlist-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all`}
                        placeholder="+34600123456"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                    
                    {/* Consent */}
                    <div>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleInputChange}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
                          aria-describedby="waitlist-consent-error"
                          aria-required="true"
                        />
                        <span className="text-sm text-gray-600">
                          He leído y acepto la{' '}
                          <a 
                            href="/politica-privacidad" 
                            target="_blank" 
                            className="text-red-500 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Leer política de privacidad (se abre en nueva pestaña)"
                          >
                            política de privacidad
                          </a>
                          . <span className="text-red-500">*</span>
                        </span>
                      </label>
                      {errors.consent && (
                        <p id="waitlist-consent-error" className="text-red-500 text-sm mt-1">{errors.consent}</p>
                      )}
                    </div>
                    
                    {/* Priority Notice */}
                    <div className="bg-red-50 p-4 rounded-xl">
                      <p className="text-sm text-red-800 font-medium">
                        🔔 Prioridad en la próxima edición
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Los inscritos en la lista de espera tendrán acceso prioritario 48 horas antes 
                        de la apertura general de inscripciones.
                      </p>
                    </div>
                    
                    {/* Privacy Notice */}
                    <div className="bg-gray-50 p-4 rounded-xl text-xs text-gray-600">
                      <p>
                        Certificación Coaching X Valores te informa que los datos de carácter personal 
                        que nos proporciones serán tratados por nosotros como responsable de esta web. 
                        La finalidad es para notificarte sobre las próximas ediciones de la certificación. 
                        Tu legitimación se realiza a través del consentimiento. 
                        Podrás ejercer tus derechos de acceso, rectificación, limitación y suprimir 
                        los datos en contacto@tonogalan.com
                      </p>
                    </div>
                    
                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 bg-red-500 text-white font-heading uppercase tracking-wider font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : 'bg-red-600'
                      }`}
                      aria-label={isSubmitting ? "Procesando..." : "Unirse a la lista de espera"}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Procesando...</span>
                        </>
                      ) : (
                        <>
                          <Bell className="w-5 h-5" />
                          <span>Unirse a la Lista de Espera</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </animated.div>
          </div>
        ) : null
      )}
    </>
  );
}